import type { SidebarGroup } from "@plerity/dashboard-shell";
import { createShell, PORTAL_URLS, type SettingsTab } from "@plerity/dashboard-shell";
import { BarChart3, Bookmark, BookOpen, LayoutDashboard, MessageSquare, Palette, Settings } from "lucide-react";
import { ChatSettingsTabContent } from "./routes/settings/SettingsTabContent";

// ============================================================================
// Shell configuration
// ============================================================================

const sidebarGroups: SidebarGroup[] = [
  {
    title: "AI Webchat",
    defaultOpen: true,
    routes: [
      {
        path: "chat/dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        loader: () => import("./routes/chat/Dashboard"),
      },
      {
        path: "chat/conversations",
        label: "Conversations",
        icon: MessageSquare,
        loader: () => import("./routes/chat/Conversations"),
      },
      {
        path: "chat/conversations/:id",
        label: "Conversation Detail",
        icon: MessageSquare,
        loader: () => import("./routes/chat/ConversationDetail"),
        hidden: true,
      },
      {
        path: "chat/widget",
        label: "Widget Config",
        icon: Palette,
        loader: () => import("./routes/chat/WidgetConfig"),
      },
      {
        path: "chat/knowledge",
        label: "Knowledge Base",
        icon: BookOpen,
        loader: () => import("./routes/chat/KnowledgeBase"),
      },
      {
        path: "chat/analytics",
        label: "Analytics",
        icon: BarChart3,
        loader: () => import("./routes/chat/Analytics"),
      },
      {
        path: "chat/canned-responses",
        label: "Canned Responses",
        icon: Bookmark,
        loader: () => import("./routes/chat/CannedResponses"),
      },
    ],
  },
  {
    // Legacy settings redirect — the Settings page is now reached via the
    // top-right avatar menu → Settings (unified UserSettingsPage).
    // The sidebar entry is hidden; the route stays registered so any existing
    // bookmark to /chat/settings transparently redirects to /settings.
    // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22 (Phase 2d).
    title: "_legacy",
    routes: [
      {
        path: "chat/settings",
        label: "Settings",
        icon: Settings,
        loader: () => import("@plerity/dashboard-shell/SettingsRedirect"),
        hidden: true,
      },
    ],
  },
];

declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/chat-admin";

// Chat-admin-specific settings tab injected into the unified UserSettingsPage.
// Reached via top-right avatar → Settings (auto-mounted at /settings by createShell).
// Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22 (Phase 2d).
const settingsTabs: SettingsTab[] = [
  {
    id: "chat-settings",
    label: "Chat Settings",
    icon: Settings,
    content: <ChatSettingsTabContent />,
  },
];

const Shell = createShell({
  name: "chat",
  displayName: "AI Webchat",
  sidebarGroups,
  defaultRoute: "/chat/dashboard",
  currentApp: "chat",
  basename,
  settingsRoute: "/settings",
  chat: { enabled: false },
  // @allow-hardcoded — Hub SSO security settings URL; cannot be env-var in CF Pages static SPA build
  securityRoute: PORTAL_URLS.SECURITY,
  auth: {
    required: true,
    entitlement: "webchat",
  },
  testerNotes: {
    enabled: true,
    contextId: "web-chat-admin",
  },
  // Unified Settings page — reached via top-right avatar → Settings.
  // Profile / Appearance / Notifications / Tester Notes / Assistant / Privacy
  // are auto-included by UserSettingsPage; we inject the chat-specific tab here.
  // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22 (Phase 2d).
  settings: {
    additionalTabs: settingsTabs,
    assistantName: "Chat Assistant",
  },
});

function App() {
  return <Shell />;
}

export default App;
